/**
 * CharacterComment Component
 *
 * Displays a conversation message: portrait on the left, text on the right, with relative timestamp.
 * Used for conversation history output from generateCharacterComments().
 * Timestamps refresh every 15 seconds to keep relative time up to date.
 */

import React, { useEffect, useState } from 'react';
import PortraitImage from './PortraitImage';
import { sanitizeHtml } from '../../utils/sanitizer';
import { formatRelativeTime } from '../../utils/helpers';

const TIMESTAMP_UPDATE_INTERVAL_MS = 15000;

export interface CharacterCommentProps {
  character?: string | undefined;
  portrait?: string | undefined;
  text?: string | undefined;
  timestamp?: string | number | undefined;
  className?: string | undefined;
  'data-testid'?: string | undefined;
}

export const CharacterComment: React.FC<CharacterCommentProps> = ({
  character = 'Unknown',
  portrait = 'generic',
  text = '',
  timestamp,
  className = '',
  'data-testid': dataTestId,
}) => {
  const [, setTick] = useState(0);

  // Refresh relative timestamp every 15 seconds
  useEffect(() => {
    if (timestamp === undefined || timestamp === '') return;
    const id = setInterval(() => setTick((t) => t + 1), TIMESTAMP_UPDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [timestamp]);

  const sanitizedText = sanitizeHtml(String(text || ''));
  const relativeTime = timestamp !== undefined && timestamp !== '' ? formatRelativeTime(timestamp) : '';

  return (
    <div
      className={`rich-text-character-comment flex items-start ${className}`}
      data-testid={dataTestId}
      role="article"
      aria-label={`Message from ${character}`}
    >
      <div className="rich-text-character-comment-portrait flex-shrink-0">
        <PortraitImage
          portraitId={portrait}
          variant="thumbnail"
          alt={character}
        />
      </div>
      <div className="rich-text-character-comment-body flex-1 min-w-0">
        <div
          className="rich-text-character-comment-text break-words"
          dangerouslySetInnerHTML={{ __html: sanitizedText }}
        />
        {(relativeTime && (
          <div className="rich-text-character-comment-footer flex items-baseline gap-2 flex-wrap mt-0.5 justify-end">
            <span className="rich-text-character-comment-timestamp text-[12px] opacity-75">
              {relativeTime}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CharacterComment;
